import { useState, useEffect, useCallback, useRef } from 'react';
import type { ApiState, ApiHookOptions, ApiErrorResponse } from '../../types/api/common';

/**
 * Generic API hook for handling API requests with state management
 * 
 * Features:
 * - Loading, error, and success states
 * - Automatic retry logic
 * - Request cancellation
 * - Cache support
 * - Optimistic updates
 * - Error recovery
 */
export function useApi<T = unknown>(
  apiCall: () => Promise<{ data: T }>,
  options: ApiHookOptions = {}
): {
  state: ApiState<T>;
  execute: () => Promise<void>;
  retry: () => Promise<void>;
  cancel: () => void;
  reset: () => void;
} {
  const {
    immediate = false,
    cache = false,
    retry = true,
    onSuccess,
    onError,
    onFinally,
  } = options;
  
  // TODO: Implement caching functionality
  void cache;

  const [state, setState] = useState<ApiState<T>>({
    data: null,
    loading: false,
    error: null,
    success: false,
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const retryCountRef = useRef(0);
  const maxRetries = 3;
  const retryDelay = 1000; // 1 second base delay

  /**
   * Execute the API call
   */
  const execute = useCallback(async (): Promise<void> => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setState(prev => ({
      ...prev,
      loading: true,
      error: null,
      success: false,
    }));

    try {
      const response = await apiCall();
      
      // Check if request was cancelled
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      setState({
        data: response.data,
        loading: false,
        error: null,
        success: true,
      });

      retryCountRef.current = 0;
      onSuccess?.(response.data);
    } catch (error) {
      // Check if request was cancelled
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      const apiError = error as ApiErrorResponse;
      
      setState({
        data: null,
        loading: false,
        error: apiError,
        success: false,
      });

      onError?.(apiError);
    } finally {
      onFinally?.();
    }
  }, [apiCall, onSuccess, onError, onFinally]);

  /**
   * Retry the API call with exponential backoff
   */
  const retryCall = useCallback(async (): Promise<void> => {
    if (!retry || retryCountRef.current >= maxRetries) {
      return;
    }

    retryCountRef.current += 1;
    const delay = retryDelay * Math.pow(2, retryCountRef.current - 1);

    // Wait before retrying
    await new Promise(resolve => setTimeout(resolve, delay));

    return execute();
  }, [execute, retry]);

  /**
   * Cancel the current request
   */
  const cancel = useCallback((): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setState(prev => ({
      ...prev,
      loading: false,
    }));
  }, []);

  /**
   * Reset the state
   */
  const reset = useCallback((): void => {
    cancel();
    setState({
      data: null,
      loading: false,
      error: null,
      success: false,
    });
    retryCountRef.current = 0;
  }, [cancel]);

  /**
   * Execute immediately if requested
   */
  useEffect(() => {
    if (immediate) {
      execute();
    }

    // Cleanup on unmount
    return () => {
      cancel();
    };
  }, [immediate, execute, cancel]);

  return {
    state,
    execute,
    retry: retryCall,
    cancel,
    reset,
  };
}

/**
 * Hook for API mutations (POST, PUT, DELETE operations)
 */
export function useMutation<TData = unknown, TVariables = unknown>(
  mutationFn: (variables: TVariables) => Promise<{ data: TData }>,
  options: {
    onSuccess?: (data: TData, variables: TVariables) => void;
    onError?: (error: ApiErrorResponse, variables: TVariables) => void;
    onFinally?: (variables: TVariables) => void;
  } = {}
): {
  state: ApiState<TData>;
  mutate: (variables: TVariables) => Promise<void>;
  reset: () => void;
} {
  const [state, setState] = useState<ApiState<TData>>({
    data: null,
    loading: false,
    error: null,
    success: false,
  });

  const { onSuccess, onError, onFinally } = options;

  const mutate = useCallback(async (variables: TVariables): Promise<void> => {
    setState({
      data: null,
      loading: true,
      error: null,
      success: false,
    });

    try {
      const response = await mutationFn(variables);
      
      setState({
        data: response.data,
        loading: false,
        error: null,
        success: true,
      });

      onSuccess?.(response.data, variables);
    } catch (error) {
      const apiError = error as ApiErrorResponse;
      
      setState({
        data: null,
        loading: false,
        error: apiError,
        success: false,
      });

      onError?.(apiError, variables);
    } finally {
      onFinally?.(variables);
    }
  }, [mutationFn, onSuccess, onError, onFinally]);

  const reset = useCallback((): void => {
    setState({
      data: null,
      loading: false,
      error: null,
      success: false,
    });
  }, []);

  return {
    state,
    mutate,
    reset,
  };
}

/**
 * Hook for optimistic updates
 */
export function useOptimisticMutation<TData = unknown, TVariables = unknown>(
  mutationFn: (variables: TVariables) => Promise<{ data: TData }>,
  optimisticUpdateFn: (variables: TVariables) => TData,
  options: {
    onSuccess?: (data: TData, variables: TVariables) => void;
    onError?: (error: ApiErrorResponse, variables: TVariables) => void;
    onFinally?: (variables: TVariables) => void;
  } = {}
): {
  state: ApiState<TData>;
  mutate: (variables: TVariables) => Promise<void>;
  reset: () => void;
} {
  const [state, setState] = useState<ApiState<TData>>({
    data: null,
    loading: false,
    error: null,
    success: false,
  });

  const { onSuccess, onError, onFinally } = options;
  const previousDataRef = useRef<TData | null>(null);

  const mutate = useCallback(async (variables: TVariables): Promise<void> => {
    // Store previous data for rollback
    previousDataRef.current = state.data;

    // Apply optimistic update
    const optimisticData = optimisticUpdateFn(variables);
    setState(prev => ({
      ...prev,
      data: optimisticData,
      loading: true,
      error: null,
    }));

    try {
      const response = await mutationFn(variables);
      
      setState({
        data: response.data,
        loading: false,
        error: null,
        success: true,
      });

      onSuccess?.(response.data, variables);
    } catch (error) {
      // Rollback optimistic update
      setState({
        data: previousDataRef.current,
        loading: false,
        error: error as ApiErrorResponse,
        success: false,
      });

      onError?.(error as ApiErrorResponse, variables);
    } finally {
      onFinally?.(variables);
    }
  }, [mutationFn, optimisticUpdateFn, onSuccess, onError, onFinally, state.data]);

  const reset = useCallback((): void => {
    setState({
      data: null,
      loading: false,
      error: null,
      success: false,
    });
    previousDataRef.current = null;
  }, []);

  return {
    state,
    mutate,
    reset,
  };
}

/**
 * Hook for polling data at regular intervals
 */
export function usePolling<T = unknown>(
  apiCall: () => Promise<{ data: T }>,
  interval: number = 5000,
  options: ApiHookOptions & { enabled?: boolean } = {}
): {
  state: ApiState<T>;
  start: () => void;
  stop: () => void;
  refetch: () => Promise<void>;
} {
  const { enabled = true, ...apiOptions } = options;
  const intervalRef = useRef<number | null>(null);
  const { state, execute } = useApi(apiCall, { ...apiOptions, immediate: enabled });

  const start = useCallback((): void => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = window.setInterval(() => {
      execute();
    }, interval);

    // Execute immediately when starting
    execute();
  }, [execute, interval]);

  const stop = useCallback((): void => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const refetch = useCallback(async (): Promise<void> => {
    return execute();
  }, [execute]);

  useEffect(() => {
    if (enabled) {
      start();
    } else {
      stop();
    }

    return () => {
      stop();
    };
  }, [enabled, start, stop]);

  return {
    state,
    start,
    stop,
    refetch,
  };
}
